import ErrorIcon from '@mui/icons-material/Error';
import PaymentsIcon from '@mui/icons-material/Payments';
import ScheduleIcon from '@mui/icons-material/Schedule';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CircularProgress from '@mui/material/CircularProgress';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useNavigate } from '@tanstack/react-location';
import useTaxPeriodsSummary from '../../taxPayments/hooks/useTaxPeriodsSummary';
import { formatDateForLocale } from '../../core/dateFormatting';

interface TaxPaymentStatusCardProps {
    propertyId: number;
}

const formatCurrency = (amount: number | null, currency: string) => {
    if (amount === null) return '-';
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency,
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
    }).format(amount);
};

const getComponentDisplayName = (componentId: string): string => {
    const names: Record<string, string> = {
        'tourism-tax': 'Tourism Tax',
        'tourism-dev': 'Tourism Dev.',
        'szja': 'SZJA',
        'szocho': 'SZOCHO',
        'tb': 'TB',
        'ipa': 'IPA',
    };
    return names[componentId] ?? componentId.toUpperCase();
};

const TaxPaymentStatusCard = ({ propertyId }: TaxPaymentStatusCardProps) => {
    const navigate = useNavigate();
    const { data, loading, error } = useTaxPeriodsSummary(propertyId);

    if (loading) {
        return (
            <Card
                sx={{
                    border: '3px solid #374151',
                    borderRadius: 0,
                    backgroundColor: '#1a1a2e',
                }}
            >
                <CardContent sx={{ p: 3, display: 'flex', justifyContent: 'center' }}>
                    <CircularProgress size={24} />
                </CardContent>
            </Card>
        );
    }

    if (error || !data) {
        return null;
    }

    if (!data.hasPreset) {
        return null; // TaxConfigurationPrompt will handle this case
    }

    return (
        <Card
            sx={{
                border: data.overdueCount > 0 ? '3px solid #ef4444' : '3px solid #374151',
                borderRadius: 0,
                backgroundColor: '#1a1a2e',
            }}
        >
            <CardContent sx={{ p: 3 }}>
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        mb: 3,
                        borderBottom: data.overdueCount > 0 ? '2px solid #ef4444' : '2px solid #f59e0b',
                        pb: 1,
                    }}
                >
                    <Stack direction="row" alignItems="center" spacing={1}>
                        <PaymentsIcon sx={{ color: data.overdueCount > 0 ? '#ef4444' : '#f59e0b' }} />
                        <Typography
                            variant="h6"
                            sx={{
                                fontWeight: 900,
                                textTransform: 'uppercase',
                                letterSpacing: '0.05em',
                            }}
                        >
                            Tax Payments
                        </Typography>
                    </Stack>
                    <Button
                        size="small"
                        onClick={() => navigate({ to: `/${propertyId}/financial/tax-payments` })}
                        sx={{
                            borderRadius: 0,
                            border: '2px solid #f59e0b',
                            color: '#f59e0b',
                            fontWeight: 700,
                            textTransform: 'uppercase',
                            letterSpacing: '0.05em',
                            fontSize: '0.75rem',
                            px: 2,
                            py: 0.5,
                            '&:hover': {
                                backgroundColor: 'rgba(245, 158, 11, 0.1)',
                                borderColor: '#f59e0b',
                            },
                        }}
                    >
                        Manage
                    </Button>
                </Box>

                {data.overdueCount > 0 && (
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            p: 2,
                            mb: 2,
                            backgroundColor: 'rgba(239, 68, 68, 0.1)',
                            border: '2px solid #ef4444',
                        }}
                    >
                        <ErrorIcon sx={{ color: '#ef4444' }} />
                        <Typography sx={{ color: '#ef4444', fontWeight: 700 }}>
                            {data.overdueCount} overdue payment{data.overdueCount > 1 ? 's' : ''}
                        </Typography>
                    </Box>
                )}

                {data.upcomingPeriods.length > 0 ? (
                    <Box>
                        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                            <ScheduleIcon sx={{ color: 'text.secondary', fontSize: 18 }} />
                            <Typography
                                variant="overline"
                                sx={{ color: 'text.secondary', fontWeight: 700 }}
                            >
                                Upcoming Due Dates
                            </Typography>
                        </Stack>
                        <Stack spacing={1}>
                            {data.upcomingPeriods.map((period) => (
                                <Box
                                    key={period.id}
                                    sx={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        p: 1.5,
                                        border: '1px solid #374151',
                                    }}
                                >
                                    <Box>
                                        <Typography sx={{ fontWeight: 600, fontSize: '0.875rem' }}>
                                            {getComponentDisplayName(period.componentId)}
                                        </Typography>
                                        <Typography
                                            variant="caption"
                                            sx={{ color: 'text.secondary' }}
                                        >
                                            Due: {formatDateForLocale(period.dueDate)}
                                        </Typography>
                                    </Box>
                                    <Typography
                                        sx={{
                                            fontWeight: 700,
                                            fontFamily: 'monospace',
                                            color: '#f59e0b',
                                        }}
                                    >
                                        {formatCurrency(period.estimatedAmount, period.currency)}
                                    </Typography>
                                </Box>
                            ))}
                        </Stack>
                    </Box>
                ) : (
                    <Typography
                        variant="body2"
                        sx={{
                            color: 'text.secondary',
                            textAlign: 'center',
                            py: 2,
                        }}
                    >
                        No upcoming tax payments. Generate periods to track your taxes.
                    </Typography>
                )}
            </CardContent>
        </Card>
    );
};

export default TaxPaymentStatusCard;
